import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Alert } from '../components/ui/Alert';
import axios from '../utils/axios';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post('/api/auth/forgot-password', { email });
      if (response.data.otp_sent) {
        navigate('/verify-otp', { state: { email, purpose: 'reset' } });
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="split-layout">
      <div className="split-layout-left">
        <div className="branding">
          <div className="branding-logo">
            <h1>MavScope</h1>
            <p>AI-Powered Roof & Siding Measurements</p>
          </div>
          
          <div className="branding-features">
            <div className="branding-feature">
              <div className="branding-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <div className="branding-feature-text">
                <h3>Secure Account Recovery</h3>
                <p>Reset your password securely with a one-time verification code.</p>
              </div>
            </div>
            
            <div className="branding-feature">
              <div className="branding-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
              </div>
              <div className="branding-feature-text">
                <h3>Mobile-Friendly</h3>
                <p>Receive a 6-digit OTP via email or SMS for quick verification.</p>
              </div>
            </div>
            
            <div className="branding-feature">
              <div className="branding-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <div className="branding-feature-text">
                <h3>Protected Access</h3>
                <p>Your account security is our priority with encrypted password storage.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="split-layout-right">
        <div style={{ width: '100%', maxWidth: '400px' }}>
          <div className="mobile-logo">
            <h1>MavScope</h1>
            <p>Account Recovery</p>
          </div>
          
          <div className="form-card">
            <div className="form-header">
              <h2 className="form-title">Forgot password?</h2>
              <p className="form-description">
                Enter your email address and we'll send you a verification code to reset your password.
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-fields">
                <Input
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  autoComplete="email"
                  autoFocus
                />

                {error && <Alert variant="error">{error}</Alert>}
              </div>

              <div className="form-actions">
                <Button 
                  type="submit" 
                  variant="primary"
                  size="lg"
                  fullWidth
                  isLoading={isLoading}
                >
                  Send Verification Code
                </Button>
              </div>
            </form>

            <div className="form-footer">
              <p>
                Remember your password?{' '}
                <Link to="/login" className="link">Sign in</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
